import { BaseSaver, SAVE_DATA_KEY } from './BaseSaver';
import { Data } from './types';

export enum ERROR_CODES {
  NO_DATA = 'No saved data could be found',
  LS_UNAVAILABLE = 'LocalStorage is not available'
}

export interface ILocalStorageSaverParams {
  getUserId: () => string;
}

export class LocalStorageSaver<T extends Data> extends BaseSaver<T> {

  protected _cache: object = null;

  /**
   * @deprecated Unused
   */
  protected _throttleMS: number = undefined;

  /**
   * @deprecated Unused
   */
  protected _throttlePromise: Promise<void> = undefined;

  private static _canUseLocalStorage: boolean = undefined;

  private readonly _getUserIdRef: () => string;

  public constructor(params: ILocalStorageSaverParams) {
    super();

    this._getUserIdRef = params.getUserId;
  }

  public save(data: Partial<T>): Promise<void> {
    if (!this.canUseLocalStorage()) {
      this.logger.debugLog(ERROR_CODES.LS_UNAVAILABLE);
      return Promise.reject(LocalStorageSaver.createErrorObject(ERROR_CODES.LS_UNAVAILABLE));
    }

    return new Promise<void>((resolve: Function, reject: Function) => {
      try {
        this.logger.debugLog('Updating cache...');
        this.updateCache(data);
        this.logger.debugLog(`Setting key ${this.saveDataKey}...`);
        window.localStorage.setItem(this.saveDataKey, JSON.stringify(this._cache));

        return resolve();
      } catch (err) {
        this.logger.debugLog(ERROR_CODES.LS_UNAVAILABLE);
        return reject(err);
      }
    });
  }

  public load(_keys?: string[]): Promise<T> {
    if (!this.canUseLocalStorage()) {
      return Promise.reject(LocalStorageSaver.createErrorObject(ERROR_CODES.LS_UNAVAILABLE));
    }

    return new Promise<T>((resolve: Function, reject: Function) => {
      let saveDataStr: string = '';

      try {
        this.logger.debugLog(`Fetching key ${this.saveDataKey}...`);
        saveDataStr = window.localStorage.getItem(this.saveDataKey);
      } catch (err) {
        return reject(err);
      }

      if (saveDataStr) {
        try {
          let saveData: T = JSON.parse(saveDataStr);
          this.logger.debugLog(`Updating cache...`);
          this.updateCache(saveData);

          if (_keys) {
            let retVal: Partial<T> = {};

            for (let i = 0; i < _keys.length; i++) {
              // @ts-ignore
              retVal[_keys[i]] = saveData[_keys[i]];
            }

            this.logger.debugLog(`Resolving with savedata...`);

            return resolve(retVal);
          } else {
            this.logger.debugLog(`Resolving with savedata...`);
            return resolve(saveData);
          }
        } catch (err) {
          return reject(err);
        }
      } else {
        return reject(LocalStorageSaver.createErrorObject(ERROR_CODES.NO_DATA));
      }
    });
  }

  public get enabled(): boolean {
    return this.canUseLocalStorage();
  }

  public clear(): Promise<void> {
    try {
      window.localStorage.removeItem(this.saveDataKey);
      return Promise.resolve();
    } catch (err) {
      this.logger.error(err);
      return Promise.reject(LocalStorageSaver.createErrorObject(ERROR_CODES.LS_UNAVAILABLE));
    }
  }

  public get saveDataKey(): string {
    return `${SAVE_DATA_KEY}_${this._getUserIdRef()}`;
  }

  private canUseLocalStorage(): boolean {
    if (typeof LocalStorageSaver._canUseLocalStorage === 'undefined') {
      try {
        const key: string = `${this.saveDataKey}_lsTest`;
        let num: string = Math.random().toString();
        window.localStorage.setItem(key, num);
        LocalStorageSaver._canUseLocalStorage = (window.localStorage.getItem(key) === num);
      } catch (err) {
        this.logger.warn(err.message);
        LocalStorageSaver._canUseLocalStorage = false;
      }
    }

    return LocalStorageSaver._canUseLocalStorage;
  }

  private static createErrorObject(code: ERROR_CODES): Error {
    return new Error(code);
  }

  public static _debugRead(key: string): any {
    if (!LocalStorageSaver._canUseLocalStorage) throw LocalStorageSaver.createErrorObject(ERROR_CODES.LS_UNAVAILABLE);

    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.error(error);
    }
  }

  public static _debugWrite(key: string, data: any): any {
    if (!LocalStorageSaver._canUseLocalStorage) {
      throw LocalStorageSaver.createErrorObject(ERROR_CODES.LS_UNAVAILABLE);
    }

    try {
      return window.localStorage.setItem(key, data);
    } catch (error) {
      console.error(error);
    }
  }

  protected throttle(): Promise<void> {
    return Promise.resolve();
  }
}
